import React from 'react';
import { Bell, User, LogOut, Settings, Moon, Sun, Database, TestTube2, Users, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HelpMenu } from '@/components/ProductTour';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Breadcrumbs } from './Breadcrumbs';
import { useTimesheetContext, useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { EmployeeImage } from '@/components/EmployeeImage';
import { useDataSource } from '@/contexts/DataSourceContext';
import { getScreenIdFromPath, getScreenNameFromId, isSettingsPath } from '@/utils/screenMapping';
import { CompanySelector } from '@/features/multi-company';

interface HeaderProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

export const Header: React.FC<HeaderProps> = ({ currentPath, onNavigate }) => {
  const { getScreenMode, getAllScreenModes } = useDataSource();
  
  const currentScreenId = getScreenIdFromPath(currentPath);
  const currentScreenName = currentScreenId ? getScreenNameFromId(currentScreenId) : null;
  const isSettings = isSettingsPath(currentPath);
  
  // Use the screen ID from path mapping (works for both regular screens and settings pages)
  const currentMode = currentScreenId ? getScreenMode(currentScreenId) : 'demo';
  const isDemo = currentMode === 'demo';
  
  const globalContext = useTimesheetContext();
  const dashboardContext = useTimesheetForScreen('dashboard', currentScreenId === 'dashboard');
  const timesheetContext = useTimesheetForScreen('timesheet', currentScreenId === 'timesheet');
  const approvalsContext = useTimesheetForScreen('approvals', currentScreenId === 'approvals');
  
  const screenContext = currentScreenId === 'dashboard' ? dashboardContext : 
                       currentScreenId === 'timesheet' ? timesheetContext :
                       currentScreenId === 'approvals' ? approvalsContext :
                       globalContext;
  
  const { currentUser, employees, switchEmployee } = screenContext;
  const [theme, setTheme] = React.useState<'light' | 'dark'>('light');
  const [open, setOpen] = React.useState(false);

  // Calculate screen mode statistics
  const allScreens = getAllScreenModes();
  const stats = {
    total: allScreens.length,
    demo: allScreens.filter(s => s.mode === 'demo').length,
    live: allScreens.filter(s => s.mode === 'live').length,
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    document.documentElement.classList.toggle('dark');
  };

  // Mock notifications count
  const notificationsCount = 3;

  return (
    <header className="sticky top-0 z-30 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-12 sm:h-14 lg:h-16 items-center gap-2 sm:gap-3 lg:gap-4 px-3 sm:px-4 lg:px-6">
        {/* Breadcrumbs */}
        <div className="flex-1 hidden md:block">
          <Breadcrumbs currentPath={currentPath} onNavigate={onNavigate} />
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Company Selector */}
          <CompanySelector />
          
          {/* Employee Selector with Search */}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-[200px] justify-between hidden lg:flex"
              >
                <Users className="h-4 w-4 mr-2 shrink-0" />
                <span className="truncate">
                  {currentUser ? currentUser.name : "Select employee"}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[250px] p-0">
              <Command>
                <CommandInput placeholder="Search employees..." />
                <CommandList>
                  <CommandEmpty>No employee found.</CommandEmpty>
                  <CommandGroup>
                    {employees.map((employee) => (
                      <CommandItem
                        key={employee.id}
                        value={`${employee.name} ${employee.role}`}
                        onSelect={() => {
                          switchEmployee(employee.id);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            currentUser?.id === employee.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col">
                          <span className="font-medium">{employee.name}</span>
                          <span className="text-xs text-muted-foreground">{employee.role}</span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Subtle Mode Indicator with Tooltip */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onNavigate('/settings/screens')}
                  className="hidden sm:inline-flex"
                >
                  <span className="text-xs text-muted-foreground/70 font-medium tracking-wide uppercase cursor-pointer hover:text-muted-foreground transition-colors">
                    {isDemo ? 'Demo' : 'Live'}
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-md">
                <div className="space-y-3">
                  {/* Current Screen Mode */}
                  {currentScreenName && !isSettings ? (
                    <>
                      <div>
                        <p className="font-bold text-sm mb-1">
                          {isDemo ? '🎭 Demo Mode' : '🔌 Live Mode'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {isDemo 
                            ? 'Viewing sample data for testing. No real data is affected.'
                            : 'Connected to production database. All changes affect real data.'
                          }
                        </p>
                      </div>
                      
                      {/* Current Screen Info */}
                      <div className="pt-2 border-t">
                        <p className="text-xs font-semibold mb-1">Current Screen:</p>
                        <p className="text-xs text-muted-foreground">{currentScreenName}</p>
                      </div>
                    </>
                  ) : (
                    <div>
                      <p className="font-bold text-sm mb-1">
                        {isSettings ? '⚙️ Settings' : '📊 Application'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {isSettings 
                          ? 'Configure screen modes and application settings.'
                          : 'Navigate to a screen to see its mode status.'
                        }
                      </p>
                    </div>
                  )}
                  
                  {/* Screen Statistics */}
                  <div className="space-y-1 text-xs pt-2 border-t">
                    <p className="font-semibold mb-1">All Screens:</p>
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-1">
                        <TestTube2 className="h-3 w-3 text-amber-600" />
                        Demo Mode:
                      </span>
                      <span className="font-medium">{stats.demo} screens</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-1">
                        <Database className="h-3 w-3 text-green-600" />
                        Live Mode:
                      </span>
                      <span className="font-medium">{stats.live} screens</span>
                    </div>
                  </div>
                  
                  {/* Action */}
                  <p className="text-xs text-muted-foreground pt-2 border-t">
                    💡 Click to configure screen modes
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Theme Toggle */}
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'light' ? (
              <Moon className="h-5 w-5" />
            ) : (
              <Sun className="h-5 w-5" />
            )}
          </Button>

          {/* Help & Support Menu */}
          <HelpMenu onNavigate={onNavigate} />

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                {notificationsCount > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                  >
                    {notificationsCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel>Notifications</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="max-h-96 overflow-y-auto">
                <div className="p-3 hover:bg-accent cursor-pointer">
                  <p className="text-sm font-medium">Timesheet Approved</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Your timesheet for week ending 15/12/2024 has been approved
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">2 hours ago</p>
                </div>
                <div className="p-3 hover:bg-accent cursor-pointer">
                  <p className="text-sm font-medium">Submission Reminder</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Don't forget to submit your timesheet by Friday
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">1 day ago</p>
                </div>
                <div className="p-3 hover:bg-accent cursor-pointer">
                  <p className="text-sm font-medium">New Project Added</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    You've been added to "Cloud Migration - Phase 2"
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">2 days ago</p>
                </div>
              </div>
              <DropdownMenuSeparator />
              <div className="p-2">
                <Button variant="ghost" className="w-full text-sm" size="sm">
                  View all notifications
                </Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2">
                <EmployeeImage 
                  name={currentUser?.name || 'User'} 
                  photoUrl={currentUser?.photoUrl}
                  size="sm"
                />
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">{currentUser?.name}</p>
                  <p className="text-xs text-muted-foreground">{currentUser?.role}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{currentUser?.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {currentUser?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onNavigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onNavigate('/settings/general')}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
